import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, extractError } from "@/lib/api";
import { ChevronLeft, MapPin, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AddressRegionFields from "@/components/kiyana/AddressRegionFields";

export default function StoreAddresses() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [region, setRegion] = useState(null);

  const load = () => api.get("/addresses").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return (
      <div className="text-center py-16">
        <MapPin className="w-12 h-12 text-cocoa-muted mx-auto mb-3" />
        <div className="text-sm text-cocoa-muted mb-4">Masuk untuk mengelola alamat</div>
        <Link to="/store/auth" className="px-6 h-11 leading-[44px] inline-block rounded-full bg-brand text-white text-sm font-semibold">Masuk</Link>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!region?.complete) return toast.error("Lengkapi wilayah alamat sampai kelurahan + detail jalan");
    const fd = new FormData(e.target);
    try {
      await api.post("/addresses", {
        name: fd.get("name"), phone: fd.get("phone"),
        address: region.composed,
        province: region.province, city: region.city, district: region.district,
        village: region.village, postal_code: region.postal_code,
        is_default: fd.get("is_default") === "on",
      });
      toast.success("Alamat tersimpan");
      setShowForm(false);
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const del = async (id) => {
    await api.delete(`/addresses/${id}`);
    toast.success("Alamat dihapus");
    load();
  };

  return (
    <div className="space-y-3 animate-fade-up">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Alamat Saya</h1>
        <div className="w-9" />
      </div>

      <button data-testid="addr-new-btn" onClick={() => setShowForm(!showForm)} className="w-full h-12 rounded-2xl bg-brand text-white text-sm font-semibold flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Tambah Alamat Baru
      </button>

      {showForm && (
        <form onSubmit={create} className="bg-white rounded-2xl border border-blush-soft p-4 space-y-2.5">
          <input name="name" data-testid="addr-form-name" placeholder="Nama penerima" required className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
          <input name="phone" data-testid="addr-form-phone" placeholder="No. HP (08xx)" required className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
          <AddressRegionFields testPrefix="addr-form" onChange={setRegion} />
          <label className="flex items-center gap-2 text-xs text-cocoa">
            <input type="checkbox" name="is_default" className="w-4 h-4 accent-[#C4707F]" /> Jadikan alamat utama
          </label>
          <button data-testid="addr-form-save" className="w-full h-11 rounded-full bg-brand text-white text-sm font-semibold">Simpan Alamat</button>
        </form>
      )}

      {items === null && <div className="text-center py-10 text-sm text-cocoa-muted">Memuat...</div>}
      {items?.map((a) => (
        <div key={a.id} data-testid={`addr-card-${a.id}`} className="bg-white rounded-2xl border border-blush-soft p-4">
          <div className="flex justify-between items-start">
            <div className="text-sm font-semibold flex items-center gap-2">
              {a.name}
              {a.is_default && <span className="text-[9px] bg-blush text-brand rounded-full px-2 py-0.5 font-semibold">Utama</span>}
            </div>
            <button data-testid={`addr-del-${a.id}`} onClick={() => del(a.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="text-xs text-cocoa-muted mt-0.5">{a.phone}</div>
          <div className="text-xs text-cocoa mt-1 leading-relaxed">{a.address}</div>
        </div>
      ))}
      {items?.length === 0 && !showForm && (
        <div className="text-center py-12 bg-white rounded-2xl border border-blush-soft text-sm text-cocoa-muted">Belum ada alamat tersimpan.</div>
      )}
    </div>
  );
}
